import { Children, type FormEvent, Fragment, PureComponent, type ReactElement, type ReactNode } from "react";

import { type ContactUsDisplayProps } from "./ContactUsPage";
import { submitForm } from "../../api/ApiHelper";
import { EMAIL_PATTERN, MAX_TEXT_LENGTH_CONTACT_FORMS } from "../../utils/constants";
import { MaterialInput } from "../../common/components/materialInput/MaterialInput";
import { trackCustomerInquiry } from "../../feature/Analytics/ga/ga";
import LabeledInput from "../../common/components/LabeledInput";
import { type WithTranslation, withTranslation as withTranslationBase } from "react-i18next";
import { HOMEPAGE, URL_CONTACT_US } from "../../utils/url/staticUrls";
import { Card } from "../../Configurators/Linear/components/Card";
import { SimpleSelect } from "../../common/components/Select/SimpleSelect";
import { Button } from "~/common/components/Button/Button";
import { TextField } from "@mui/material";

interface ContactUsFormProps extends WithTranslation, Omit<ContactUsDisplayProps, "subjects"> {
  subjects: {
    label: string;
    value: string;
  }[];
}

interface ContactUsFormState {
  message: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
  selectedTopic?: string;
  selectedTitle?: string;
  selectedSalutation?: string;
  validationErrors: Partial<ValidationErrorFields>;
}

enum ValidationFields {
  SELECTED_TOPIC,
  MESSAGE,
  NAME,
  SURNAME,
  EMAIL,
}

type ValidationErrorFields = { [key in ValidationFields]: string };

const FormControl = (props: { children?: ReactNode }): ReactNode => {
  return Children.map(props.children, (child) => {
    // not to add margin on labels
    if ((child as ReactElement).type === LabeledInput) {
      return child;
    } else {
      return <div className="mb-3">{child}</div>;
    }
  });
};

class ContactUsForm extends PureComponent<ContactUsFormProps, ContactUsFormState> {
  constructor(props: ContactUsFormProps) {
    super(props);

    this.state = {
      message: "",
      name: props.user.firstName ?? "",
      surname: props.user.lastName ?? "",
      email: props.user.email ?? "",
      phone: props.user.phone ?? "",
      selectedTopic: props.user.title ?? "",
      selectedTitle: props.user.title ?? "",
      selectedSalutation: props.user.salutation ?? "",
      validationErrors: {},
    };

    this.validate = this.validate.bind(this);
  }

  validate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const { t } = this.props;

    const validationErrors = {};

    if (!this.state.selectedTopic) {
      validationErrors[ValidationFields.SELECTED_TOPIC] = t("page.contactUs.form.error.subjectValue.notEmpty");
    }
    if (!this.state.message) {
      validationErrors[ValidationFields.MESSAGE] = t("page.contactUs.form.error.message.notEmpty");
    }
    if (!this.state.name) {
      validationErrors[ValidationFields.NAME] = t("page.contactUs.form.error.firstName.notEmpty");
    }
    if (!this.state.surname) {
      validationErrors[ValidationFields.SURNAME] = t("page.contactUs.form.error.lastName.notEmpty");
    }
    if (!this.state.email || !validateEmail(this.state.email)) {
      validationErrors[ValidationFields.EMAIL] = t("page.contactUs.form.error.email.pattern");
    }

    this.setState({ validationErrors: validationErrors });

    if (Object.keys(validationErrors).length === 0) {
      const formElement = document.getElementById("submitForm");
      trackCustomerInquiry(this.state.selectedTopic!);
      submitForm(formElement);
    }
  }

  render() {
    const { t, subjects, salutations, titles } = this.props;
    const { message, name, surname, email, phone, selectedTopic, selectedSalutation, selectedTitle, validationErrors } =
      this.state;

    const endpointUrl = URL_CONTACT_US;

    const ffPrefix = "contactUsPage.form.fields";

    const selectedTopicTranslation: string = selectedTopic ? t(selectedTopic) ?? "" : "";

    const remodeledTitlesArray = titles.map((obj) => {
      const labelTranslated = t(obj.label as string);
      return { value: obj.value, label: labelTranslated };
    });

    const remodeledSalutationsArray = salutations.map((obj) => {
      const labelTranslated = t(obj.label as string);
      return { value: obj.value, label: labelTranslated };
    });

    return (
      <Fragment>
        <Card className={"p-30"} disableHover={true}>
          <form id="submitForm" action={endpointUrl} method="POST">
            <input id="csrfInput" type="hidden" data-type="csrf" />
            <input name="subjectValue" type="hidden" value={selectedTopic} />
            <input name="subjectTranslated" type="hidden" value={selectedTopicTranslation} />
            <input name="message" type="hidden" value={message} />
            <input name="salutation" type="hidden" value={selectedSalutation} />
            <input name="title" type="hidden" value={selectedTitle} />
            <input name="firstName" type="hidden" value={name} />
            <input name="lastName" type="hidden" value={surname} />
            <input name="email" type="hidden" value={email} />
            <input name="phone" type="hidden" value={phone} />
          </form>
          <form className="scha__contact-us-form" onSubmit={this.validate}>
            <div className="row">
              <div className="col-6">
                <FormControl>
                  <SimpleSelect
                    className={"mb-3"}
                    shrink={true}
                    values={subjects}
                    label={t(`${ffPrefix}.subject.label`)}
                    id={"contact_subject_dropdown"}
                    value={selectedTopic}
                    onChange={(e) => this.setState({ selectedTopic: e.target.value as string })}
                    placeholder={t(`${ffPrefix}.subject.placeholder`)}
                    error={!!validationErrors[ValidationFields.SELECTED_TOPIC]}
                    fullWidth={true}
                    helperText={validationErrors[ValidationFields.SELECTED_TOPIC]}
                  />
                  <TextField
                    variant={"outlined"}
                    multiline
                    fullWidth
                    inputProps={{ sx: { height: "200px !important" }, maxLength: MAX_TEXT_LENGTH_CONTACT_FORMS }}
                    label={t(`${ffPrefix}.message.label`)}
                    value={message}
                    id={"contact_message_field"}
                    className={`scha__contact-us-form__message`}
                    onChange={(e) => this.setState({ message: e.target.value })}
                    placeholder={t(`${ffPrefix}.message.placeholder`)}
                    error={!!validationErrors[ValidationFields.MESSAGE]}
                    helperText={validationErrors[ValidationFields.MESSAGE]}
                  />
                </FormControl>
              </div>

              <div className="col-6">
                <FormControl>
                  <SimpleSelect
                    shrink={true}
                    fullWidth={true}
                    label={t(`${ffPrefix}.title.label`)}
                    className="mb-3"
                    values={remodeledTitlesArray}
                    id={"contact_title_dropdown"}
                    value={selectedTitle}
                    onChange={(e) => this.setState({ selectedTitle: e.target.value as string })}
                    placeholder={t(`${ffPrefix}.title.placeholder`)}
                  />
                  <SimpleSelect
                    shrink={true}
                    fullWidth={true}
                    label={t(`${ffPrefix}.salutation.label`)}
                    className="mb-3"
                    values={remodeledSalutationsArray}
                    id={"contact_salution_dropdown"}
                    value={selectedSalutation}
                    onChange={(e) => this.setState({ selectedSalutation: e.target.value as string })}
                    placeholder={t(`${ffPrefix}.salutation.placeholder`)}
                  />
                  <MaterialInput
                    value={name}
                    id={"contact_name_field"}
                    onChange={(e) => this.setState({ name: e.target.value })}
                    error={!!validationErrors[ValidationFields.NAME]}
                    helperLabel={validationErrors[ValidationFields.NAME]}
                    label={t(`${ffPrefix}.name.label`)}
                  />
                  <MaterialInput
                    value={surname}
                    id={"contact_surname_field"}
                    onChange={(e) => this.setState({ surname: e.target.value })}
                    name="lastName"
                    error={!!validationErrors[ValidationFields.SURNAME]}
                    helperLabel={validationErrors[ValidationFields.SURNAME]}
                    label={t(`${ffPrefix}.surname.label`)}
                  />
                  <MaterialInput
                    value={email}
                    id={"contact_email_field"}
                    onChange={(e) => this.setState({ email: e.target.value })}
                    error={!!validationErrors[ValidationFields.EMAIL]}
                    helperLabel={validationErrors[ValidationFields.EMAIL]}
                    label={t(`${ffPrefix}.email.label`)}
                  />
                  <MaterialInput
                    value={phone}
                    id={"contact_phone_field"}
                    onChange={(e) => this.setState({ phone: e.target.value })}
                    label={t(`${ffPrefix}.phone.label`)}
                  />
                </FormControl>
              </div>

              <div className="col-12 mt-3">
                <div className="d-flex flex-justify-between flex-align-center">
                  <span className="flex-2">{t("contactUsPage.form.mandatory")}</span>
                  <div className="d-flex flex-1">
                    <Button
                      color={"secondary"}
                      className="scha__contact-us-form__button"
                      id={"contact_cancel_btn"}
                      href={HOMEPAGE}
                      fullWidth={true}
                    >
                      {t("contactUsPage.form.cancel")}
                    </Button>
                    <Button
                      className="scha__contact-us-form__button ml-4"
                      id={"contact_send_btn"}
                      type="submit"
                      fullWidth={true}
                    >
                      {t("contactUsPage.form.ok")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Card>
      </Fragment>
    );
  }
}

export function validateEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email);
}

export default withTranslationBase()(ContactUsForm);
